import { closestCenter, DndContext } from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { ColumnDef } from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation } from '#router'
import { FeaturebaseBugButton } from '@/components/featurebase-bug-button'
import { ForkStarButton } from '@/components/problem-lists/fork-star-button'
import { MergeButton } from '@/components/problem-lists/merge-button'
import { DifficultyCell } from '@/components/problems/difficulty-cell'
import { ProblemRowKebabMenu } from '@/components/problems/problem-row-kebab-menu'
import { ProblemsFilterPopover } from '@/components/problems/problems-filter-popover'
import { ProblemsSearchBar } from '@/components/problems/problems-search-bar'
import { ProblemsSortPopover } from '@/components/problems/problems-sort-popover'
import { Button } from '@/components/ui/button'
import { ProgressStatusIcon } from '@/components/ui/progress-status-icon'
import { ProgressSummary } from '@/components/ui/progress-summary'
import { REVEAL_ON_HOVER_DECORATIVE } from '@/components/ui/reveal-on-hover'
import { cn } from '@/lib/utils'
import { ShuffleButton } from '../shuffle-button'
import '@/styles/problems-table.css'
import { AddProblemsModal } from './add-problems-modal'
import { DeleteListDialog } from './delete-list-dialog'
import { EditListModal } from './edit-list-modal'
import { ProblemListSidebar } from './problem-list-sidebar'
import {
  type DisplayProblem,
  LIST_DETAIL_SORT_POPOVER_OPTIONS,
  useListDetail,
} from './use-list-detail'

// ---------------------------------------------------------------------------
// GripHandle — drag handle for custom sort reordering
// ---------------------------------------------------------------------------

function GripHandle() {
  return (
    <span
      className={cn(
        'text-muted-foreground flex-shrink-0',
        REVEAL_ON_HOVER_DECORATIVE,
      )}
    >
      <GripVertical className="h-4 w-4" />
    </span>
  )
}

// ---------------------------------------------------------------------------
// SortableTableRow — wraps a <tr> with dnd-kit sortable behavior
// ---------------------------------------------------------------------------

function SortableTableRow({
  id,
  children,
  onClick,
}: {
  id: string
  children: React.ReactNode
  onClick: () => void
}) {
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    attributes,
    listeners,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
  }

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`group cursor-grab touch-none active:cursor-grabbing ${isDragging ? 'bg-muted/70' : ''}`}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button, a')) return
        onClick()
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </tr>
  )
}

// ---------------------------------------------------------------------------
// ListDetailPage
// ---------------------------------------------------------------------------

export function ListDetailPage() {
  const location = useLocation()
  const listId = location.pathname.match(/^\/problems\/lists\/([^/]+)/)?.[1]

  if (!listId) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)]">
        <ProblemListSidebar />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-muted-foreground">List not found.</div>
        </div>
      </div>
    )
  }

  return <ListDetailPageInner listId={listId} />
}

function ListDetailPageInner({ listId }: { listId: string }) {
  const { navigate } = useLocation()

  const {
    data,
    isLoading,
    error,
    isSignedIn,
    userProgress,
    problems,
    displayProblems,
    solvedCount,
    progressProblems,
    progressRecord,
    filters,
    searchQuery,
    matchMode,
    showTags,
    missingLists,
    setSearchQuery,
    setMatchMode,
    setShowTags,
    toggleFilterValue,
    resetFilters,
    setFilterOperator,
    availableTopics,
    availableDifficulties,
    availableLists,
    hasSearch,
    hasActiveFilters,
    isCustomSort,
    isDragEnabled,
    sensors,
    handleDragEnd,
    handleRemove,
    handleMoveToTop,
    handleMoveToBottom,
    handleSortChange,
    showAddProblems,
    setShowAddProblems,
    handleAddProblemsOpenChange,
    showEdit,
    setShowEdit,
    showDelete,
    setShowDelete,
    dismissed,
    setDismissed,
  } = useListDetail(listId, 'bctci-list-detail')

  // --- TanStack Table columns ---

  const columns: ColumnDef<DisplayProblem>[] = useMemo(() => {
    const cols: ColumnDef<DisplayProblem>[] = []

    // Grip handle column (only in custom sort with no active search/filter)
    if (isDragEnabled) {
      cols.push({
        id: 'grip',
        size: 32,
        header: '',
        cell: () => <GripHandle />,
      })
    }

    // Title column with status indicator
    cols.push({
      id: 'title',
      header: 'Title',
      cell: ({ row }) => {
        const p = row.original
        const status = userProgress?.progress?.[p.id]
        const via = userProgress?.solvedVia?.[p.id]
        return (
          <span className="inline-flex items-center gap-2 font-medium">
            <span className="inline-flex w-4 shrink-0 justify-center">
              <ProgressStatusIcon status={status} viaTitle={via?.title} />
            </span>
            <span>
              {p.externalId && (
                <span className="text-muted-foreground">
                  {Number(p.externalId)}.{' '}
                </span>
              )}
              {p.title}
            </span>
            {showTags && p.topics?.length > 0 && (
              <span className="inline-flex gap-1">
                {p.topics.map((t) => (
                  <span
                    key={t.slug}
                    className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs"
                  >
                    {t.name}
                  </span>
                ))}
              </span>
            )}
          </span>
        )
      },
    })

    // Difficulty column
    cols.push({
      id: 'difficulty',
      header: () => <span className="block text-right">Difficulty</span>,
      size: 80,
      cell: ({ row }) => (
        <DifficultyCell difficulty={row.original.difficulty} />
      ),
    })

    // Actions column (kebab menu)
    cols.push({
      id: 'actions',
      size: 40,
      header: '',
      cell: ({ row }) => (
        <ProblemRowKebabMenu
          onRemove={() => handleRemove(row.original.id)}
          onAddToList={() => setShowAddProblems(true)}
          onMoveToTop={
            isCustomSort ? () => handleMoveToTop(row.original.id) : undefined
          }
          onMoveToBottom={
            isCustomSort ? () => handleMoveToBottom(row.original.id) : undefined
          }
        />
      ),
    })

    return cols
  }, [
    isDragEnabled,
    userProgress,
    showTags,
    isCustomSort,
    handleRemove,
    handleMoveToTop,
    handleMoveToBottom,
    setShowAddProblems,
  ])

  // --- TanStack Table instance ---

  const table = useReactTable({
    data: displayProblems,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  // --- Loading state (after all hooks) ---

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)]">
        <ProblemListSidebar />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-muted-foreground">Loading...</div>
        </div>
      </div>
    )
  }

  // Error states — distinguish NOT_FOUND from generic errors
  if (error) {
    const isNotFound = error.data?.code === 'NOT_FOUND'
    return (
      <div className="flex min-h-[calc(100vh-4rem)]">
        <ProblemListSidebar />
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <h2 className="text-lg font-semibold">
            {isNotFound ? 'List not found' : 'Unable to load list'}
          </h2>
          <p className="text-muted-foreground text-sm">
            {isNotFound
              ? "This list doesn't exist or you don't have access to it."
              : 'There was a problem loading this list. Please try again.'}
          </p>
          <div className="flex items-center gap-3">
            {!isNotFound && (
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            )}
            <Link
              href="/problems"
              className="text-primary text-sm hover:underline"
            >
              Back to Problems
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (!data) return null

  const { list, stats } = data

  // --- Table element ---

  const tableElement = (
    <div className="overflow-x-auto">
      <table className="problems-table w-full">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="text-muted-foreground py-2 text-left text-xs font-medium"
                  style={
                    header.column.id !== 'title'
                      ? { width: header.column.getSize() }
                      : undefined
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => {
              const rowContent = row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className="py-3 text-sm"
                  style={
                    cell.column.id !== 'title'
                      ? { width: cell.column.getSize() }
                      : undefined
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))

              if (isDragEnabled) {
                return (
                  <SortableTableRow
                    key={row.id}
                    id={row.original.id}
                    onClick={() =>
                      navigate(`/editor?problem=${row.original.slug}`)
                    }
                  >
                    {rowContent}
                  </SortableTableRow>
                )
              }

              return (
                <tr
                  key={row.id}
                  className="group cursor-pointer"
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button, a')) return
                    navigate(`/editor?problem=${row.original.slug}`)
                  }}
                >
                  {rowContent}
                </tr>
              )
            })
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="text-muted-foreground py-10 text-center"
              >
                {hasSearch || hasActiveFilters
                  ? 'No problems match your search or filters.'
                  : 'No problems in this list yet. Click the + button to add some.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )

  return (
    <div className="flex min-h-[calc(100vh-4rem)]">
      <ProblemListSidebar />
      <div className="flex-1 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 py-8">
          {/* Two-column layout: left = header/progress, right = table */}
          <div className="flex gap-8">
            {/* Left column — header, actions, description, progress */}
            <div className="border-border w-80 shrink-0 self-start rounded-lg border p-5">
              <h1 className="text-2xl font-bold">{list.name}</h1>
              <div className="text-muted-foreground mt-1 text-sm">
                {stats.total} question{stats.total !== 1 ? 's' : ''}
              </div>

              {/* Action icons */}
              <div className="mt-3 flex items-center gap-2">
                <Button
                  size="icon-sm"
                  variant="outline"
                  title="Add problems"
                  onClick={() => setShowAddProblems(true)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <Button
                  size="icon-sm"
                  variant="outline"
                  title="Edit list"
                  onClick={() => setShowEdit(true)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                {!list.isSystem && (
                  <Button
                    size="icon-sm"
                    variant="outline"
                    title="Delete list"
                    onClick={() => setShowDelete(true)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
                <ForkStarButton sourceType="list" sourceId={list.id} />
                <MergeButton sourceType="list" sourceId={list.id} />
              </div>

              <div className="mt-3">
                {list.description && (
                  <p className="text-muted-foreground text-sm">
                    {list.description}
                  </p>
                )}
              </div>

              {/* Divider */}
              <div className="bg-border my-4 h-px w-full" />

              {/* Progress widget */}
              <div>
                <ProgressSummary
                  problems={progressProblems}
                  progress={progressRecord}
                />
              </div>
            </div>

            {/* Right column — toolbar + scrollable problem table */}
            <div className="min-w-0 flex-1">
              {/* Missing lists banner */}
              {!dismissed && missingLists.length > 0 && (
                <div className="mb-4 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                  <span>
                    Some filters reference lists you don&apos;t have — your view
                    may differ.
                  </span>
                  <button
                    type="button"
                    onClick={() => setDismissed(true)}
                    className="ml-4 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {/* Toolbar */}
              <div className="mb-4 flex items-center gap-1">
                <ProblemsSearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                />
                <ProblemsSortPopover
                  sortBy={list.sortBy}
                  sortDir={list.sortDir as 'asc' | 'desc'}
                  onSortChange={handleSortChange}
                  alwaysActive
                  defaultValue="custom"
                  sortOptions={LIST_DETAIL_SORT_POPOVER_OPTIONS}
                  showTags={showTags}
                  onToggleTags={() => setShowTags((v) => !v)}
                />
                <ProblemsFilterPopover
                  filters={filters}
                  matchMode={matchMode}
                  onMatchModeChange={setMatchMode}
                  onToggleFilter={toggleFilterValue}
                  onOperatorChange={setFilterOperator}
                  availableTopics={availableTopics}
                  availableDifficulties={availableDifficulties}
                  availableLists={availableLists}
                  onReset={resetFilters}
                />
                <div className="flex-1" />
                {isSignedIn && <FeaturebaseBugButton />}
                {isSignedIn && userProgress && (
                  <span className="text-muted-foreground inline-flex items-center gap-1.5 text-sm">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      className="shrink-0"
                    >
                      <circle
                        cx="9"
                        cy="9"
                        r="7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        opacity="0.25"
                      />
                      <circle
                        cx="9"
                        cy="9"
                        r="7"
                        fill="none"
                        stroke="#429539"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeDasharray={2 * Math.PI * 7}
                        strokeDashoffset={
                          2 *
                          Math.PI *
                          7 *
                          (1 - solvedCount / (problems.length || 1))
                        }
                        transform="rotate(-90 9 9)"
                      />
                    </svg>
                    {solvedCount}/{problems.length} Solved
                  </span>
                )}
                <ShuffleButton
                  problems={displayProblems}
                  listId={listId}
                  ariaLabel="Solve a random problem from this list"
                />
              </div>

              {/* Problems table */}
              <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
                {isDragEnabled ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={displayProblems.map((p) => p.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {tableElement}
                    </SortableContext>
                  </DndContext>
                ) : (
                  tableElement
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <EditListModal
        open={showEdit}
        onOpenChange={setShowEdit}
        listId={list.id}
        currentName={list.name}
        currentDescription={list.description}
      />
      {!list.isSystem && (
        <DeleteListDialog
          open={showDelete}
          onOpenChange={setShowDelete}
          listId={list.id}
          listName={list.name}
        />
      )}

      <AddProblemsModal
        open={showAddProblems}
        onOpenChange={handleAddProblemsOpenChange}
        listId={listId}
      />
    </div>
  )
}
